import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';

import { GRID_COLUMNS, PlotDraft } from './plot.models';
import { formatPystQuery } from './pyst-format';
import { QueryTestDialogComponent } from './query-test-dialog.component';

export interface PlotEditorData {
    title: string;
    draft: PlotDraft;
}

@Component({
    selector: 'app-plot-editor-dialog',
    templateUrl: './plot-editor-dialog.component.html',
    styleUrls: ['./plot-editor-dialog.component.scss'],
})
export class PlotEditorDialogComponent {
    /**
     * Running a query changes nothing that is saved. Editing, testing and editing
     * again as often as you like leaves the stored plot untouched until Save, and
     * Cancel discards the lot — which is the point of testing in place rather than
     * saving to find out. The result opens over this dialog, so the query being
     * edited and the result being judged are not fighting for the same screen.
     */
    test(): void {
        const query = this.draft.query.trim();
        if (!query) {
            return;
        }
        this.dialog.open(QueryTestDialogComponent, { data: { query }, autoFocus: false });
    }

    /** Grid spans offered for the width, labelled as a share of the page. */
    public readonly colOptions = Array.from({ length: GRID_COLUMNS }, (_, index) => {
        const cols = index + 1;
        const share = cols === GRID_COLUMNS ? 'full width'
            : cols === GRID_COLUMNS / 2 ? 'half'
            : cols === GRID_COLUMNS / 3 ? 'third'
            : cols === GRID_COLUMNS / 4 ? 'quarter'
            : cols === (GRID_COLUMNS * 2) / 3 ? 'two thirds'
            : cols === (GRID_COLUMNS * 3) / 4 ? 'three quarters'
            : `${Math.round((cols / GRID_COLUMNS) * 100)}%`;
        return { cols, label: `${cols} / ${GRID_COLUMNS} — ${share}` };
    });

    public draft: PlotDraft;

    constructor(
        private dialogRef: MatDialogRef<PlotEditorDialogComponent, PlotDraft>,
        private dialog: MatDialog,
        @Inject(MAT_DIALOG_DATA) public data: PlotEditorData,
    ) {
        // Open pretty-printed, the way the back of a card shows it. Whitespace-only,
        // so the stored query's meaning is unchanged either way.
        this.draft = { ...data.draft, query: formatPystQuery(data.draft.query) };
    }

    get valid(): boolean {
        return this.draft.query.trim().length > 0;
    }

    /**
     * Macro arguments are `$name(value)`. A `name: value` spelling parses as a
     * positional argument, and pyst then rejects the call with "expects 0 positional
     * arguments" — a confusing error a long way from its cause, so catch it here.
     */
    get syntaxWarning(): string | null {
        const query = this.draft.query;
        // Not an error: a figure spec is JSON, and a card shows JSON as a table.
        // Worth saying, because someone who wanted a drawn plot has the wrong flag.
        if (/\$as_json\(\s*true\s*\)/i.test(query)) {
            return 'This returns a plotly figure spec as JSON, so the card will show it as data. For a drawn plot, set $as_json(false) or remove it.';
        }
        const macro = /~[A-Za-z_][A-Za-z0-9_]*\(/.exec(query);
        if (!macro) {
            return null;
        }
        const args = query.slice(macro.index + macro[0].length);
        const colonArg = /(^|[(,\s])([A-Za-z_][A-Za-z0-9_]*)\s*:/.exec(args);
        if (!colonArg) {
            return null;
        }
        const name = colonArg[2];
        return `Macro arguments use $name(value), not "${name}: value" — write $${name}(...) instead.`;
    }

    save(): void {
        if (!this.valid) {
            return;
        }
        this.dialogRef.close({
            ...this.draft,
            name: this.draft.name.trim() || 'Untitled',
            query: this.draft.query.trim(),
        });
    }

    cancel(): void {
        this.dialogRef.close();
    }
}
